import React, { useEffect, useState, useMemo } from 'react';
import cronstrue from 'cronstrue';
import { BarChart, Bar, PieChart, Pie, Cell, Tooltip, Legend, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Box, Grid2 as Grid, Typography, Card, CardContent, TableContainer, Table, TableCell, TableRow, TableHead, TableBody, Paper } from '@mui/material';
import { getAppUsersAll } from '../../api/appUsersApi.js'
import { useDomain } from '../../context/DomainContext.js';
import { getAppUserAllById } from '../../api/appWorkplacesUsersApi.js';
import { getAppSchedulerJobsByDomainId } from '../../api/schedulerJobsApi.js';
import { useLoader } from '../../context/LoaderContext.js';

const COLORS = ['#0088FE', '#6b6b6b', '#FFBB28', '#FF8042'];

const Dashboard = () => {
  const [users, setUsers] = useState([]);
  const [workplaceUsers, setWorkplaceUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const { selectedDomain } = useDomain();
  const { showLoader, hideLoader } = useLoader();

  const dataUserTemplates = useMemo(() => {
    const usersWithTemplate = workplaceUsers.filter(
      (user) => user.signature_template_id
    ).length;

    const usersWithoutTemplate = workplaceUsers.length - usersWithTemplate;

    return [
      { name: "Con firma", value: usersWithTemplate },
      { name: "Sin firma", value: usersWithoutTemplate },
    ];
  }, [workplaceUsers]);

  useEffect(() => {
    if (selectedDomain) {
      showLoader();
      const fetchData = async () => {
        try {
          const responses = await Promise.allSettled([
            getAppUsersAll(),
            getAppUserAllById(selectedDomain),
            getAppSchedulerJobsByDomainId(selectedDomain)
          ]);
          setUsers(responses[0]?.value?.data || []);
          setWorkplaceUsers(responses[1]?.value?.data || []);
          setTasks(responses[2]?.value?.data?.slice(0, 3) || []);

        } catch (error) {
          console.error("Error general:", error);
          setUsers([]);
          setWorkplaceUsers([]);
          setTasks([]);
        } finally {
          hideLoader();
        }
      };

      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  const userRoles = [
    { name: 'Admins', value: users.filter((u) => u.role === 'admin').length },
    { name: 'Lectores', value: users.filter((u) => u.role === 'viewer').length },
  ];

  const workplaceActiveInactive = [
    { name: 'Activos', value: workplaceUsers.filter((u) => u.is_active).length },
    { name: 'Inactivos', value: workplaceUsers.filter((u) => !u.is_active).length },
  ];

  const getSchedulerName = (name) => {
    return name.match(/jobs\/([^/]+)$/)?.[1]
  }

  return (
    <Box
      padding={4}
      sx={{
        maxHeight: 'calc(100vh - 234px)',
        overflow: "auto"
      }}
    >
      <Grid container spacing={3}>
        <Grid item="true" size={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usuarios Administradores
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userRoles}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    label
                  >
                    {Array.isArray(userRoles) && userRoles.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item="true" size={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cuentas de dominio
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={workplaceActiveInactive}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    label
                  >
                    {Array.isArray(workplaceActiveInactive) && workplaceActiveInactive.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item="true" size={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cuentas con firma
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  width={500}
                  height={300}
                  data={dataUserTemplates}
                  margin={{ top: 20, right: 30, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8" name="Cuentas" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item="true" size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tareas Programadas
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Horario</TableCell>
                      <TableCell>Último Intento</TableCell>
                      <TableCell>Zona Horaria</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(tasks) && tasks.length > 0 ? tasks.map((task) => (
                      <TableRow key={task?.name || ''}>
                        <TableCell>{task?.name ? getSchedulerName(task.name) : ''}</TableCell>
                        <TableCell>{task?.description || ''}</TableCell>
                        <TableCell>{task?.state ? (task.state === "ENABLED" ? "Habilitado" : "No Habilitado") : ''}</TableCell>
                        <TableCell>{task?.schedule ? cronstrue.toString(task.schedule, { locale: "es" }) : ''}</TableCell>
                        <TableCell>{task?.lastAttemptTime?.seconds ? new Date(task.lastAttemptTime.seconds * 1000).toLocaleString() : ''}</TableCell>
                        <TableCell>{task?.timeZone || ''}</TableCell>
                      </TableRow>
                    )) : <TableRow key={'emptyrow'}>
                      <TableCell colSpan={6} align='center'>Sin Tareas</TableCell>
                    </TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
